import React, { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import dayjs from "dayjs";
import { splitOptions, mergeOptionLines } from "../question-papers/create/paperBuilder";
import { useSnackbar } from "../../providers/SnackbarProvider";
import api from "../../api/client";
import { OverAllLoader } from "../../components/OverAllLoader";
import { LoadingOverlay } from "../../components/LoadingOverlay";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import EGCanvasTool from "./EGCanvasTool";
import CodingTool from "./CodingTool";

import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Paper,
  TextField,
  IconButton,
  Radio,
  Chip,
  Tooltip,
  LinearProgress,
} from "@mui/material";

import {
  Timer as TimerIcon,
  Videocam as VideocamIcon,
  Block as BlockIcon,
  Assignment as AssignmentIcon,
  CloseFullscreen as CloseFullscreenIcon,
  ReportGmailerrorred as ReportGmailerrorredIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Mic as MicIcon,
  Pause as PauseIcon,
  Flag as FlagIcon,
  FlagOutlined as FlagOutlinedIcon,
} from "@mui/icons-material";
import { getLabelByKey, useChangeableItems } from "store/useDynamicLabel";

/* ---------------- Project theme ---------------- */
const C = {
  primary: "#1c97e3",
  primaryDark: "#1565c0",
  ink: "#1a2b3c",
  muted: "#7b8794",
  line: "#e3e9f0",
  bg: "#f4f7fb",
  green: "#16a34a",
  greenBg: "#dcfce7",
  amber: "#b45309",
  amberBg: "#fef3c7",
  red: "#d32f2f",
  indigo: "#4f46e5",
  indigoBg: "#eef2ff",
};

const tagBase = { height: 22, fontSize: 11, fontWeight: 700, borderRadius: "8px" };

/* ---------------- AI evaluation overlay (blocks interaction while EvaluateAnswer/ is in flight) ---------------- */
/* ---------------- Markdown question text ---------------- */
const MarkdownQuestion = ({ text }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      p: ({ children }) => (
        <Typography sx={{ fontSize: "15px", fontWeight: 600, lineHeight: 1.6, color: C.ink, mb: 1 }}>
          {children}
        </Typography>
      ),
      li: ({ children }) => (
        <Typography component="li" sx={{ mb: 0.5, lineHeight: 1.6, color: C.ink }}>{children}</Typography>
      ),
      code: ({ inline, children }) => {
        const value = String(children);
        const block = !inline && value.includes("\n");
        return block ? (
          <Box sx={{ backgroundColor: "#1e293b", color: "#f1f5f9", p: 1.5, my: 1.5, borderRadius: "8px", overflowX: "auto", fontFamily: "monospace", fontSize: 13 }}>
            <pre style={{ margin: 0 }}><code>{value}</code></pre>
          </Box>
        ) : (
          <Box component="span" sx={{ backgroundColor: "#eef1f5", color: C.primaryDark, borderRadius: "6px", fontFamily: "monospace", fontSize: 13, px: 0.75, py: 0.25, mx: 0.25 }}>
            {value}
          </Box>
        );
      },
    }}
  >
    {text}
  </ReactMarkdown>
);

/* ---------------- Normalize QuestionsForStudent response -> render model ----------------
   Backend item types: single | either-or | split | either-or-split.
   Internal model: { number, kind: single|split|either-or, marks, parts[] | choices[] }.   */
// Options may be an array OR a newline-separated string ("(a) ...\n(b) ...").
const parseOptions = (opts) => {
  if (Array.isArray(opts)) return opts.length ? mergeOptionLines(opts) : null;
  if (typeof opts === "string" && opts.trim()) {
    const grouped = splitOptions(opts);
    return grouped.length ? grouped : null;
  }
  return null;
};

const normalizePart = (q, subLabel) =>
  q
    ? {
      quiz_id: q.question_id,
      question_id: q.question_id,
      question: q.question,
      question_type: q.question_type,
      marks: q.marks,
      options: parseOptions(q.options),
      image_url: q.image_url || null,
      sub_label: subLabel || q.sub_label || null,
      tool_type: q.tool_type || null,
      coding: q.coding ? { ...q.coding, testcase_results: q.testcases || q.coding.testcase_results || null } : null,
      // Delivery `chosen`: true if the student already answered this in the current
      // attempt (false on a fresh attempt). Used to restore state on resume.
      chosen: !!q.chosen,
      // Previously saved answer values for the current attempt — used to hydrate the
      // UI on resume (backend is the source of truth, see quiz contract §Reopen flow).
      saved_answer: q.answer ?? null,
      saved_design_answer: q.student_design_answer ?? null,
      saved_image_url: q.design_answer ?? null,
    }
    : null;

const normalizePaper = (data) => {
  const sumMarks = (parts) => parts.reduce((m, p) => m + (p?.marks || 0), 0);
  const sections = (data?.sections || []).map((sec, si) => {
    const items = (sec.questions || []).map((it) => {
      const base = { number: it.question_number };
      switch (it.type) {
        case "split": {
          const parts = (it.sub_parts || []).map((s) => normalizePart(s, s.sub_label));
          // Split sub-parts share the same chosen value (answered at the question level).
          return { ...base, kind: "split", marks: sumMarks(parts), parts, chosen: parts.some((p) => p?.chosen) };
        }
        case "either-or": {
          const a = normalizePart(it.option_a), b = normalizePart(it.option_b);
          // `chosen` lives on each option; pre-select the one the student already picked.
          const chosenOption = a?.chosen ? "a" : b?.chosen ? "b" : null;
          return {
            ...base, kind: "either-or", marks: a?.marks || 0,
            choices: [{ label: "a", parts: [a] }, { label: "b", parts: [b] }],
            chosenOption, chosen: !!chosenOption
          };
        }
        case "either-or-split": {
          const aParts = (it.option_a || []).map((s) => normalizePart(s, s.sub_label));
          const bParts = (it.option_b || []).map((s) => normalizePart(s, s.sub_label));
          // All sub-parts of an option share its chosen value → pick the chosen option group.
          const chosenOption = aParts.some((p) => p?.chosen) ? "a" : bParts.some((p) => p?.chosen) ? "b" : null;
          return {
            ...base, kind: "either-or", marks: sumMarks(aParts),
            choices: [{ label: "a", parts: aParts }, { label: "b", parts: bParts }],
            chosenOption, chosen: !!chosenOption
          };
        }
        case "single":
        default: {
          const p = normalizePart(it);
          return { ...base, kind: "single", marks: p?.marks || 0, parts: [p], chosen: !!p?.chosen };
        }
      }
    });
    // Choice config: student is shown total_given questions but answers only must_answer.
    const choice =
      sec.choice && sec.choice.must_answer
        ? { total_given: sec.choice.total_given ?? items.length, must_answer: sec.choice.must_answer }
        : null;
    return {
      paper_section_id: sec.paper_section_id ?? sec.section_order ?? si,
      section_name: sec.section_name,
      pattern: sec.pattern,
      instructions: sec.instructions || [],
      start_number: sec.start_number,
      choice,
      section_total_marks: items.reduce((m, it) => m + (it.marks || 0), 0),
      questions: items,
    };
  });
  return {
    title: data?.category,
    exam_title: data?.exam_title || "Assessment",
    subject_code: data?.subject_code || "",
    duration_minutes: data?.time_limit != null ? parseFloat(data.time_limit) : null,
    total_marks: data?.total_marks,
    sections,
  };
};

const Test = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const webcamRef = useRef(null);
  const camStreamRef = useRef(null);

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const { assessment_id, cameraAction, test_type, scheduled_date, scheduled_time, entry_time_minutes } = location.state || {};
  const userData = JSON.parse(localStorage.getItem("userData")) || {};
  const userId = userData?.user_id || null;
  const isProctored = test_type === "Proctored Assessment";

  const [started, setStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false); // AI EvaluateAnswer/ in flight
  const [evalProgress, setEvalProgress] = useState(null); // { received, total } SSE batch progress
  const [cameraAllowed, setCameraAllowed] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [openWarning, setOpenWarning] = useState(false);
  const [openFinishDialog, setOpenFinishDialog] = useState(false);

  // Paper content (new JSON) + answers
  const [paper, setPaper] = useState(null);              // { title, sections: [...] }
  const [activeSection, setActiveSection] = useState(0);
  const [answers, setAnswers] = useState({});            // quiz_id -> { answer, design_answer, image_url }
  const [eoChoice, setEoChoice] = useState({});          // item.number -> "a" | "b"
  const [flagged, setFlagged] = useState({});            // item.number -> bool

  // Backend attempt (source of truth for resume / submit — see quiz contract).
  const [attemptId, setAttemptId] = useState(null);
  const [saveState, setSaveState] = useState({});        // quiz_id -> "saving" | "saved" | "error"
  const resolvedAssessmentId = assessment_id || +localStorage.getItem("AssessmentId") || null;

  const time = localStorage.getItem("duration");
  const [timeLeft, setTimeLeft] = useState((Number(time) || 0) * 60); // duration is in minutes

  // Speech recognition
  const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();
  const [dictating, setDictating] = useState(null);      // quiz_id being dictated
  const dictateBaseRef = useRef("");

  // Refs for use inside event-listener closures + autosave callbacks (stale-state safe)
  const attemptIdRef = useRef(null);
  const saveTimersRef = useRef({}); // quiz_id -> debounce timeout (descriptive autosave)
  useEffect(() => { attemptIdRef.current = attemptId; }, [attemptId]);

  // Cancel any pending debounced autosaves when the screen unmounts.
  useEffect(() => () => {
    Object.values(saveTimersRef.current).forEach((t) => clearTimeout(t));
  }, []);

  useEffect(() => { localStorage.removeItem("fullscreen"); }, []);
  useEffect(() => {
    if (sessionStorage.getItem("selfTestInProgress")) exitToList();
  }, []);

  // Release mic + camera when the exam screen unmounts (covers any exit path).
  useEffect(() => () => {
    try { SpeechRecognition.abortListening(); } catch (e) { /* ignore */ }
    try { webcamRef.current?.stream?.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
    try { camStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { localStorage.setItem("fullscreen", "true"); });

  /* ---------------- Instructions ---------------- */
  const instructions = [
    { icon: <TimerIcon sx={{ color: C.primary }} />, text: `${getLabel('EXAM')} duration is ${time} ${Number(time) === 1 ? "minute" : "minutes"}.` },
    ...(cameraAction === "YES" ? [{ icon: <VideocamIcon sx={{ color: C.primary }} />, text: "Webcam must remain ON throughout the test." }] : []),
    { icon: <BlockIcon sx={{ color: C.primary }} />, text: "Do not switch tabs or open other applications during the test." },
    { icon: <CloseFullscreenIcon sx={{ color: C.red }} />, text: "Switching tabs will automatically submit the test." },
    { icon: <AssignmentIcon sx={{ color: C.primary }} />, text: "For either-or questions, choose and answer ONE option only." },
    ...(cameraAction === "YES" ? [{ icon: <ReportGmailerrorredIcon sx={{ color: C.red }} />, text: "Any malpractice will lead to immediate disqualification." }] : []),
  ];

  /* ---------------- Timer ---------------- */
  const endAtRef = useRef(null);
  useEffect(() => {
    if (!started) return;
    if (!endAtRef.current) endAtRef.current = Date.now() + timeLeft * 1000;
    if (timeLeft <= 0) { setOpenFinishDialog(true); return; }
    const t = setInterval(
      () => setTimeLeft(Math.max(0, Math.ceil((endAtRef.current - Date.now()) / 1000))),
      1000
    );
    return () => clearInterval(t);
  }, [timeLeft, started]);

  const startAt = scheduled_date && scheduled_time ? dayjs(`${scheduled_date}T${scheduled_time}`) : null;
  const entryEndAt = startAt && entry_time_minutes ? startAt.add(Number(entry_time_minutes), "minute") : null;
  const [now, setNow] = useState(() => dayjs());
  useEffect(() => {
    if (started || !startAt) return;
    const t = setInterval(() => setNow(dayjs()), 1000);
    return () => clearInterval(t);
  }, [started, startAt?.valueOf()]);
  const secondsToStart = startAt ? Math.max(0, Math.ceil(startAt.diff(now) / 1000)) : 0;
  const entryClosed = !!entryEndAt && now.isAfter(entryEndAt);

  /* ---- Block refresh / close / right-click while AI evaluation is in flight ---- */
  useEffect(() => {
    if (!evaluating) return;
    const blockUnload = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    const blockKeys = (e) => {
      // F5, Ctrl+R / Cmd+R, Ctrl+Shift+R, Ctrl+W / Cmd+W
      const k = (e.key || "").toLowerCase();
      if (e.key === "F5" || ((e.ctrlKey || e.metaKey) && (k === "r" || k === "w"))) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const blockContext = (e) => e.preventDefault();
    window.addEventListener("beforeunload", blockUnload);
    window.addEventListener("keydown", blockKeys, true);
    window.addEventListener("contextmenu", blockContext);
    return () => {
      window.removeEventListener("beforeunload", blockUnload);
      window.removeEventListener("keydown", blockKeys, true);
      window.removeEventListener("contextmenu", blockContext);
    };
  }, [evaluating]);

  const formatTime = (s) => {
    const sec = Math.max(0, s);
    const h = Math.floor(sec / 3600);
    const m = String(Math.floor((sec % 3600) / 60)).padStart(2, "0");
    const ss = String(sec % 60).padStart(2, "0");
    return h > 0 ? `${String(h).padStart(2, "0")}:${m}:${ss}` : `${m}:${ss}`;
  };

  /* ---------------- Load questions ---------------- */
  const dbQuestions = async () => {
    setLoading(true);
    try {
      const res = await api.post("QuestionsForStudent", { user_id: userId, assessment_id });
      if (res?.data?.status === "success") {
        // Normalize attempt fields with `data.*` → top-level fallback (sectioned vs
        // legacy flat papers — see quiz contract §2). attempt_id is the backend's
        // source-of-truth handle for autosave + submit; persist it before starting.
        const d = res?.data || {};
        const attempt_id = d?.data?.attempt_id ?? d?.attempt_id ?? null;
        setAttemptId(attempt_id);
        attemptIdRef.current = attempt_id;

        const normalized = normalizePaper(res?.data?.data || {});
        setPaper(normalized);

        // Hydrate the UI from any previously saved answers for this attempt (resume).
        // Walk every answerable part (single / split / either-or options) and restore
        // its saved text / design payload / captured image.
        const seededAnswers = {};
        const partsOf = (it) =>
          it.kind === "either-or"
            ? (it.choices || []).flatMap((c) => c.parts || [])
            : (it.parts || []);
        (normalized.sections || []).forEach((sec) =>
          (sec.questions || []).forEach((it) =>
            partsOf(it).forEach((p) => {
              if (!p) return;
              const a = {};
              if (p.saved_answer != null) a.answer = p.saved_answer;
              if (p.saved_design_answer != null) a.design_answer = p.saved_design_answer;
              if (p.saved_image_url != null) a.image_url = p.saved_image_url;
              if (Object.keys(a).length) seededAnswers[p.quiz_id] = a;
            })
          )
        );
        if (Object.keys(seededAnswers).length) setAnswers(seededAnswers);

        // Resume: pre-select the either-or option the student already committed to.
        // Prefer the delivered `chosen` flag; fall back to whichever option carries a
        // saved answer (the contract notes click-only choice isn't persisted on its own).
        const seededChoice = {};
        (normalized.sections || []).forEach((sec) =>
          (sec.questions || []).forEach((it) => {
            if (it.kind !== "either-or") return;
            if (it.chosenOption) {
              seededChoice[it.number] = it.chosenOption;
              return;
            }
            const hasSaved = (opt) =>
              (opt?.parts || []).some((p) => p && seededAnswers[p.quiz_id]);
            const a = (it.choices || []).find((c) => c.label === "a");
            const b = (it.choices || []).find((c) => c.label === "b");
            if (hasSaved(a)) seededChoice[it.number] = "a";
            else if (hasSaved(b)) seededChoice[it.number] = "b";
          })
        );
        if (Object.keys(seededChoice).length) setEoChoice(seededChoice);
        setActiveSection(0);
        if (normalized.duration_minutes) setTimeLeft(Number(normalized.duration_minutes) * 60); // minutes → seconds
        setStarted(true);
        if (!isProctored) sessionStorage.setItem("selfTestInProgress", String(resolvedAssessmentId));
        setLoading(false);
      } else {
        setLoading(false);
        showSnackbar("error", res?.data?.message);
        exitToList();
      }
    } catch (e) {
      console.log("error:", e);
      setLoading(false);
      showSnackbar("error", "Network Failed!");
    }
  };

  // Release mic (speech recognition) + camera so the browser indicator turns off.
  const stopMedia = () => {
    try { SpeechRecognition.abortListening(); } catch (e) { /* ignore */ }
    setDictating(null);
    try { webcamRef.current?.stream?.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
    try { camStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
  };

  // <Webcam> is the single camera consumer. Capture its stream so teardown can stop it,
  // and detect the camera being unplugged / disabled mid-test.
  const handleUserMedia = (stream) => {
    camStreamRef.current = stream;
    stream.getVideoTracks().forEach((track) => {
      track.onended = () =>
        showSnackbar("error", "Camera disconnected. Please reconnect your camera to continue.");
    });
  };
  const handleUserMediaError = () =>
    showSnackbar("error", "Camera access lost. Please allow the camera to continue the test.");

  const exitToList = () => {
    stopMedia();
    localStorage.removeItem("fullscreen");
    sessionStorage.removeItem("selfTestInProgress");
    navigate("/assesment", { state: { login: true } });
    if (document.fullscreenElement) {
      (document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen)?.call(document);
    }
  };

  const handleStartTest = async () => {
    try {
      if (cameraAction === "YES") {
        // Probe permission before loading the exam, then release immediately so the
        // visible <Webcam> below is the only consumer (avoids opening the device twice).
        const probe = await navigator.mediaDevices.getUserMedia({ video: true });
        probe.getTracks().forEach((t) => t.stop());
        setCameraAllowed(true);
      }
      if (isProctored) {
        const elem = document.documentElement;
        (elem.requestFullscreen || elem.webkitRequestFullscreen || elem.msRequestFullscreen)?.call(elem);
      }
      dbQuestions();
    } catch (err) {
      console.error("Camera denied:", err);
      showSnackbar("error", "Camera access denied! Please allow camera to continue.");
    }
  };

  /* ---------------- Answer helpers ---------------- */
  const setAnswer = (quizId, patch) =>
    setAnswers((prev) => ({ ...prev, [quizId]: { ...prev[quizId], ...patch } }));

  /* ---------------- Autosave (SaveAssessmentAnswer/) ----------------
     Every meaningful answer change is persisted to the backend immediately so the
     attempt can be resumed and submitted from server state (see quiz contract §3).
     `fields` is either { answer } (text/MCQ/fill/descriptive) or
     { student_design_answer, design_answer } (lab/design). We retry transient
     failures and only flag a question "saved" after an HTTP 200 success. */
  const persistAnswer = async (quizId, fields) => {
    const aid = attemptIdRef.current;
    if (!aid || !quizId) return; // can't autosave without a valid attempt
    setSaveState((s) => ({ ...s, [quizId]: "saving" }));
    const body = {
      user_id: userId,
      assessment_id: resolvedAssessmentId,
      attempt_id: aid,
      question_id: quizId,
      ...fields,
    };
    const MAX_ATTEMPTS = 3;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        const res = await api.post("SaveAssessmentAnswer/", body);
        if (res?.data?.status === "success") {
          setSaveState((s) => ({ ...s, [quizId]: "saved" }));
          return;
        }
        throw new Error(res?.data?.message || "save failed");
      } catch (e) {
        if (attempt === MAX_ATTEMPTS) {
          setSaveState((s) => ({ ...s, [quizId]: "error" }));
          return;
        }
        await new Promise((r) => setTimeout(r, 500 * attempt)); // backoff before retry
      }
    }
  };

  // MCQ / option clicks → save immediately.
  const saveChoice = (quizId, answer) => persistAnswer(quizId, { answer });

  // Descriptive / fill text → debounce so we don't fire on every keystroke.
  const saveTextDebounced = (quizId, answer) => {
    clearTimeout(saveTimersRef.current[quizId]);
    saveTimersRef.current[quizId] = setTimeout(
      () => persistAnswer(quizId, { answer }),
      700
    );
  };

  const saveCodeDebounced = (quizId, answer, languageId) => {
    clearTimeout(saveTimersRef.current[quizId]);
    saveTimersRef.current[quizId] = setTimeout(
      () => persistAnswer(quizId, { answer, language_id: languageId }),
      700
    );
  };

  // Lab / design capture → save the design payload + captured image URL.
  const saveDesign = (quizId, designAnswer, imageUrl) =>
    persistAnswer(quizId, { student_design_answer: designAnswer, design_answer: imageUrl });

  // Fire any pending debounced text saves right now (called before submit).
  const flushPendingSaves = () => {
    Object.entries(saveTimersRef.current).forEach(([quizId, timer]) => {
      if (!timer) return;
      clearTimeout(timer);
      saveTimersRef.current[quizId] = null;
      const a = answers[quizId] || {};
      persistAnswer(quizId, { answer: a.answer ?? "" });
    });
  };

  const partAnswered = (p) => !!(answers[p.quiz_id]?.answer && String(answers[p.quiz_id].answer).trim());

  const itemAnswered = (item) => {
    if (item.kind === "either-or") {
      const ch = eoChoice[item.number];
      if (ch) {
        const opt = (item.choices || []).find((c) => c.label === ch);
        if ((opt?.parts || []).every(partAnswered)) return true;
      }
      // Resume: backend marked this pair already answered (chosen) even before the
      // student re-engages — counts toward progress and the must_answer cap.
      return !!item.chosen;
    }
    if ((item.parts || []).every(partAnswered)) return true;
    return !!item.chosen; // resume: list / single / split already answered
  };

  /* ---------------- Speech-to-text (per field) ---------------- */
  const toggleDictate = (quizId) => {
    if (dictating === quizId) {
      SpeechRecognition.stopListening();
      setDictating(null);
    } else {
      dictateBaseRef.current = answers[quizId]?.answer || "";
      resetTranscript();
      SpeechRecognition.startListening({ continuous: true, language: "en-US" });
      setDictating(quizId);
    }
  };
  useEffect(() => {
    if (dictating && listening) {
      const base = dictateBaseRef.current;
      const value = (base ? base + " " : "") + transcript;
      setAnswer(dictating, { answer: value });
      saveTextDebounced(dictating, value); // persist dictated text (debounced)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript]);

  /* ---------------- Proctoring guards ---------------- */
  useEffect(() => {
    if (!started) return;
    if (!isProctored) return;
    const onVis = () => { if (document.hidden) handleSubmit(); };
    const onBlur = () => { if (document.activeElement instanceof HTMLIFrameElement) return; setOpenWarning(true); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlur);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("blur", onBlur); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started]);

  useEffect(() => {
    if (!started || !isProctored) return;
    const onFs = () => { if (!document.fullscreenElement) setOpenWarning(true); };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started]);

  useEffect(() => {
    if (!started) return;
    if (!isProctored) {
      const onLeave = () => sessionStorage.removeItem("selfTestInProgress");
      window.addEventListener("popstate", onLeave);
      return () => window.removeEventListener("popstate", onLeave);
    }
    window.history.pushState(null, document.title, window.location.href);
    const onBack = () => {
      window.history.pushState(null, document.title, window.location.href);
      showSnackbar("warning", "Back navigation is disabled during the test!");
    };
    window.addEventListener("popstate", onBack);
    return () => window.removeEventListener("popstate", onBack);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started]);

  const handleCancelMinimize = () => {
    setOpenWarning(false);
    const elem = document.documentElement;
    (elem.requestFullscreen || elem.webkitRequestFullscreen || elem.msRequestFullscreen)?.call(elem);
  };

  /* ---------------- Submit ----------------
     Answers are autosaved to the attempt throughout the test, so the backend
     evaluates the persisted attempt rows directly by `attempt_id` — we no longer
     resend the full question-answer body (quiz contract §4). */
  const buildPayload = () => ({
    user_id: userId,
    assessment_id: resolvedAssessmentId,
    attempt_id: attemptIdRef.current,
  });

  const submittedRef = useRef(false);

  // EvaluateAnswer/ now streams Server-Sent Events: `batch_result` (per 5-question
  // batch, may arrive out of order), `batch_error`, and a final `done` event carrying
  // the global totals + full per_question set. We accumulate and return the `done`
  // payload (same shape the old single-JSON response had). axios can't stream a body,
  // so we use fetch — attaching the same Bearer token the axios interceptor would.
  const streamEvaluateAnswer = async (payload, { onBatch } = {}) => {
    const base = (api.defaults.baseURL || "").replace(/\/+$/, "");
    const token = localStorage.getItem("token");
    const response = await fetch(`${base}/EvaluateAnswer/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    // Pre-stream failure (non-2xx). 401 → expired session: mirror the axios interceptor.
    if (!response.ok || !response.body) {
      if (response.status === 401) {
        localStorage.clear();
        sessionStorage.setItem("authMessage", "Session expired. Please log in again.");
        window.location.href = "/";
        throw new Error("Session expired");
      }
      let message = "Evaluation failed. Please try again.";
      try {
        message = (await response.json())?.message || message;
      } catch (e) {
        /* non-JSON error body */
      }
      throw new Error(message);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let received = 0;
    let doneEvent = null;

    // Parse one SSE frame (may hold one or more `data:` lines) and route by type.
    const handleFrame = (frame) => {
      const data = frame
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l.startsWith("data:"))
        .map((l) => l.slice(l.indexOf("data:") + 5).trim())
        .join("");
      if (!data) return;
      let event;
      try {
        event = JSON.parse(data);
      } catch (e) {
        return; // skip a malformed / partial chunk
      }
      if (event.type === "batch_result" || event.type === "batch_error") {
        // Count every processed batch (success OR failure) so the % reaches 100.
        received += 1;
        onBatch?.({ received, total: event.total_batches });
      } else if (event.type === "done") {
        doneEvent = event;
      }
    };

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split("\n\n");
      buffer = frames.pop() || ""; // keep the trailing incomplete frame
      frames.forEach(handleFrame);
    }
    if (buffer.trim()) handleFrame(buffer); // flush any final buffered frame

    if (!doneEvent) throw new Error("Evaluation did not complete. Please try again.");
    return doneEvent;
  };

  // One EvaluateAnswer/ attempt. Resolves the final `done` event, throws on error.
  const evaluateOnce = async () => {
    // Capture any open design canvases and flush pending debounced text saves so the
    // very latest edits are persisted before the backend evaluates the attempt rows.
    window.dispatchEvent(new Event("trigger-all-captures"));
    flushPendingSaves();
    await new Promise((r) => setTimeout(r, 1500)); // let the final autosaves land
    return streamEvaluateAnswer(buildPayload(), {
      onBatch: (p) => setEvalProgress(p),
    });
  };

  const handleSubmit = async () => {
    if (submittedRef.current) return; // guard: EvaluateAnswer must fire only once
    // Never submit without a valid attempt handle (quiz contract §Recommended notes).
    if (!attemptIdRef.current) {
      showSnackbar("error", "Your attempt isn't ready yet. Please wait a moment and try again.");
      return;
    }
    submittedRef.current = true;
    setIsDisabled(true);
    setEvaluating(true);          // show AI overlay + activate refresh blockers
    setEvalProgress(null);
    setOpenWarning(false);
    setOpenFinishDialog(false);

    const MAX_ATTEMPTS = 2;       // initial call + 1 automatic retry on error
    let lastMessage = "";
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        const done = await evaluateOnce();
        // `done.status` is "success" when ≥1 batch saved (lowercase now, was "Success");
        // "error" + questions_evaluated 0 means every batch failed.
        if (String(done?.status).toLowerCase() === "success" && (done?.questions_evaluated ?? 0) > 0) {
          const failed = Number(done?.questions_failed || 0);
          showSnackbar(
            failed > 0 ? "warning" : "success",
            failed > 0
              ? `Submitted. ${failed} question${failed === 1 ? "" : "s"} could not be evaluated.`
              : (done?.message || "Answers evaluated and saved successfully.")
          );
          submittedRef.current = false;
          setEvaluating(false);
          setEvalProgress(null);
          exitToList();
          return;
        }
        // Reachable but nothing evaluated — retry if attempts remain.
        lastMessage = done?.message || "Evaluation failed. Please try again.";
      } catch (e) {
        console.log("Error:", e);
        lastMessage = "Evaluation failed. Please try again.";
      }
      if (attempt < MAX_ATTEMPTS) {
        await new Promise((r) => setTimeout(r, 1500)); // brief pause before auto-retry
      }
    }

    // All attempts exhausted → turn off the loader and stay on the test screen.
    showSnackbar("error", lastMessage);
    setEvaluating(false);
    setEvalProgress(null);
    setIsDisabled(false);
    submittedRef.current = false;
    if (endAtRef.current && Date.now() >= endAtRef.current) setOpenFinishDialog(true);
  };

  const timeUp = started && timeLeft <= 0;
  useEffect(() => {
    if (timeUp) handleSubmit();
  }, [timeUp]);

  /* ---------------- Derived ---------------- */
  const sections = paper?.sections || [];
  const section = sections[activeSection];
  const totalItems = sections.reduce((s, sec) => s + (sec.questions?.length || 0), 0);
  const answeredCount = sections.reduce(
    (s, sec) => s + (sec.questions || []).filter(itemAnswered).length, 0
  );

  const scrollToItem = (n) => document.getElementById(`q-${n}`)?.scrollIntoView({ behavior: "smooth", block: "start" });

  if (!browserSupportsSpeechRecognition) {
    // Speech is optional; warn once but still allow the exam.
    console.warn("Speech recognition not supported in this browser.");
  }

  /* ===================== RENDER ===================== */

  // ---- a single answerable part (MCQ radios or textarea + mic + canvas) ----
  const renderPartInput = (part, { disabled } = {}) => {
    const isMcq = (part.question_type || "").toLowerCase() === "mcq";
    const a = answers[part.quiz_id] || {};
    if (isMcq) {
      const opts = Array.isArray(part.options) ? part.options : [];
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {opts.map((opt, idx) => {
            const val = opt?.label ?? opt;
            const text = opt?.text ?? opt;
            const multiline = String(text ?? "").includes("\n");
            const sel = a.answer === val;
            return (
              <Box
                key={idx}
                // Clicking the selected option again deselects it (clears the answer).
                // MCQ autosaves immediately on click (quiz contract §3).
                onClick={() => {
                  if (disabled) return;
                  const next = sel ? "" : val;
                  setAnswer(part.quiz_id, { answer: next });
                  saveChoice(part.quiz_id, next);
                }}
                sx={{
                  display: "flex", alignItems: multiline ? "flex-start" : "center", gap: 1, px: 1.5, py: 1.25,
                  border: "1px solid", borderColor: sel ? C.primary : "#ccd5e0",
                  borderRadius: "10px", cursor: disabled ? "not-allowed" : "pointer",
                  backgroundColor: sel ? "#e3f2fd" : "#fff", opacity: disabled ? 0.55 : 1,
                  transition: "all .15s", "&:hover": disabled ? {} : { borderColor: C.primary, backgroundColor: "#f5faff" },
                }}
              >
                <Radio checked={sel} disabled={disabled} size="small" sx={{ p: 0.5 }} />
                <Typography sx={{ userSelect: "none", fontSize: 14, whiteSpace: "pre-wrap", ...(multiline ? { fontFamily: "monospace" } : {}) }}>
                  {opt?.label ? `${opt.label}) ` : ""}{text}
                </Typography>
              </Box>
            );
          })}
          <SaveIndicator quizId={part.quiz_id} />
        </Box>
      );
    }

    // Programming-based
    if (part.question_type === "Programming-based" && part.coding?.enabled) {
      return (
        <Box sx={{ mt: 1.5, opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? "none" : "auto" }}>
          <CodingTool
            key={part.quiz_id}
            codingData={part.coding}
            initialAnswer={a.answer}
            disabled={disabled}
            userId={userId}
            assessmentId={resolvedAssessmentId}
            attemptId={attemptIdRef.current}
            questionId={part.quiz_id}
            onSave={(val, langId) => {
              setAnswer(part.quiz_id, { answer: val });
              saveCodeDebounced(part.quiz_id, val, langId);
            }}
          />
          <SaveIndicator quizId={part.quiz_id} />
        </Box>
      );
    }

    // descriptive / fill / design
    return (
      <Box>
        <TextField
          multiline minRows={4}
          fullWidth
          disabled={disabled}
          placeholder="Write your answer here..."
          value={a.answer || ""}
          // Descriptive / fill autosaves on change with a debounce (quiz contract §3).
          onChange={(e) => {
            const v = e.target.value;
            setAnswer(part.quiz_id, { answer: v });
            saveTextDebounced(part.quiz_id, v);
          }}
          onCopy={(e) => e.preventDefault()}
          onPaste={(e) => e.preventDefault()}
          sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fbfdff" } }}
        />
        <SaveIndicator quizId={part.quiz_id} />
        {!disabled && browserSupportsSpeechRecognition && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <IconButton
              onClick={() => toggleDictate(part.quiz_id)}
              size="small"
              sx={{ backgroundColor: dictating === part.quiz_id ? C.red : C.primary, color: "#fff", "&:hover": { backgroundColor: dictating === part.quiz_id ? "#b71c1c" : C.primaryDark } }}
            >
              {dictating === part.quiz_id ? <PauseIcon fontSize="small" /> : <MicIcon fontSize="small" />}
            </IconButton>
            <Typography variant="caption" sx={{ color: C.muted }}>
              {dictating === part.quiz_id ? "Listening… click to stop" : "Click mic to dictate"}
            </Typography>
          </Box>
        )}
        {part.tool_type === "egcanvas" && (
          <Box sx={{ mt: 1.5, opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? "none" : "auto" }}>
            <EGCanvasTool
              initialData={part.question_code || a.design_answer}
              // Lab/design autosaves after each capture (quiz contract §3).
              onCapture={(d) => {
                setAnswer(part.quiz_id, { design_answer: d, image_url: d });
                saveDesign(part.quiz_id, d, d);
              }}
            />
          </Box>
        )}
      </Box>
    );
  };

  const TypeBadge = ({ t }) =>
    t ? <Chip size="small" label={t} sx={{ ...tagBase, backgroundColor: C.indigoBg, color: C.indigo }} /> : null;

  // Per-question autosave status (only shown once a save has been attempted).
  const SaveIndicator = ({ quizId }) => {
    const st = saveState[quizId];
    if (!st) return null;
    const map = {
      saving: { text: "Saving…", color: C.muted },
      saved: { text: "✓ Saved", color: C.green },
      error: { text: "⚠ Couldn't save — change again to retry", color: C.red },
    };
    const m = map[st];
    return <Typography sx={{ fontSize: 11, fontWeight: 700, color: m.color, mt: 0.75 }}>{m.text}</Typography>;
  };

  const QImage = ({ src }) =>
    src ? (
      <Box sx={{ my: 1.5, display: "flex", justifyContent: "center", border: `1px solid ${C.line}`, borderRadius: "8px", p: 1.5, backgroundColor: "#fcfdff" }}>
        <Box component="img" src={src} alt="diagram" sx={{ maxWidth: "100%", maxHeight: 300, objectFit: "contain", borderRadius: "4px" }} />
      </Box>
    ) : null;

  // ---- one numbered item (single / split / either-or) ----
  const renderItem = (item, opts = {}) => {
    const locked = !!opts.locked; // list+choice: answer limit reached & this one is unanswered
    const capped = !!opts.capped; // section has an "answer N of M" choice cap
    const isFlagged = !!flagged[item.number];
    const head = (
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontWeight: 800, color: C.primary, fontSize: 15 }}>Q{item.number}.</Typography>
          <Button
            size="small" startIcon={isFlagged ? <FlagIcon sx={{ color: "#eab308" }} /> : <FlagOutlinedIcon />}
            onClick={() => setFlagged((p) => ({ ...p, [item.number]: !p[item.number] }))}
            sx={{ textTransform: "none", color: isFlagged ? "#a16207" : C.muted, fontSize: 12 }}
          >
            {isFlagged ? "Flagged" : "Flag"}
          </Button>
          {locked && (
            <Chip size="small" label="Skipped — limit reached" sx={{ ...tagBase, backgroundColor: "#f1f5f9", color: "#94a3b8" }} />
          )}
        </Box>
        <Chip size="small" label={`${item.marks} Marks`} sx={{ ...tagBase, backgroundColor: "#ede9fe", color: "#7c3aed" }} />
      </Box>
    );
    // Dim + disable inputs for a locked (over-limit, unanswered) choice question.
    const lockSx = locked ? { opacity: 0.55 } : {};

    if (item.kind === "single") {
      const p = item.parts[0];
      return (
        <Box id={`q-${item.number}`} sx={{ ...cardSx, ...lockSx }}>
          {head}
          <TypeBadge t={p.question_type} />
          <Box sx={{ mt: 0.5 }}><MarkdownQuestion text={p.question} /></Box>
          <QImage src={p.image_url} />
          {renderPartInput(p, { disabled: locked })}
        </Box>
      );
    }

    if (item.kind === "split") {
      return (
        <Box id={`q-${item.number}`} sx={{ ...cardSx, ...lockSx }}>
          {head}
          {item.parts.map((p) => (
            <Box key={p.quiz_id} sx={{ borderLeft: `3px solid ${C.indigoBg}`, pl: 2, mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.indigo }}>({p.sub_label})</Typography>
                <Chip size="small" label={`${p.marks} Marks`} sx={{ ...tagBase, backgroundColor: "#ede9fe", color: "#7c3aed" }} />
                <TypeBadge t={p.question_type} />
              </Box>
              <MarkdownQuestion text={p.question} />
              <QImage src={p.image_url} />
              {renderPartInput(p, { disabled: locked })}
            </Box>
          ))}
        </Box>
      );
    }

    // either-or
    const ch = eoChoice[item.number];
    return (
      <Box id={`q-${item.number}`} sx={{ ...cardSx, border: `1px solid ${C.amberBg}`, ...lockSx }}>
        {head}
        <Box sx={{ backgroundColor: "#fffbeb", border: `1px solid ${C.amberBg}`, borderRadius: "8px", px: 1.5, py: 1, mb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.amber }}>Choose and answer EITHER (a) OR (b).</Typography>
          {/* In a choice-capped section (answer N of M), deselecting this pair frees the
              slot so the student can switch to a different either-or question. */}
          {capped && ch && (
            <Button
              size="small"
              onClick={() => setEoChoice((p) => ({ ...p, [item.number]: null }))}
              sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: C.amber, whiteSpace: "nowrap", minWidth: 0 }}
            >
              ✕ Clear selection
            </Button>
          )}
        </Box>
        {(item.choices || []).map((opt) => {
          const chosen = ch === opt.label;
          // Disable when this option isn't the picked one, OR when the section
          // answer-limit is reached and this whole question is over the cap.
          const disabled = locked || (ch && !chosen);
          return (
            <Box key={opt.label} sx={{ border: "1px dashed", borderColor: chosen ? C.primary : C.line, borderRadius: "10px", p: 2, mb: 1.5, opacity: disabled ? 0.5 : 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography sx={{ fontWeight: 800, color: C.amber }}>Option ({opt.label})</Typography>
                <Button
                  size="small" variant={chosen ? "contained" : "outlined"}
                  disabled={locked}
                  onClick={() => setEoChoice((p) => ({ ...p, [item.number]: opt.label }))}
                  sx={{ textTransform: "none", borderRadius: "8px", ...(chosen ? { backgroundColor: C.primary } : { borderColor: C.primary, color: C.primary }) }}
                >
                  {chosen ? `✓ Answering (${opt.label})` : `Choose (${opt.label})`}
                </Button>
              </Box>
              {opt.parts.map((p) => (
                <Box key={p.quiz_id} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    {p.sub_label && <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.indigo }}>({p.sub_label})</Typography>}
                    {p.sub_label && <Chip size="small" label={`${p.marks} Marks`} sx={{ ...tagBase, backgroundColor: "#ede9fe", color: "#7c3aed" }} />}
                    <TypeBadge t={p.question_type} />
                  </Box>
                  <MarkdownQuestion text={p.question} />
                  <QImage src={p.image_url} />
                  {renderPartInput(p, { disabled })}
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: C.bg }}>
      {/* AI evaluation overlay — sits on top of the current screen so it returns intact on error */}
      <LoadingOverlay
        open={evaluating}
        title="AI is evaluating your answers"
        progress={
          evalProgress && evalProgress.total
            ? Math.min(100, Math.round((evalProgress.received / evalProgress.total) * 100))
            : 0
        }
        message="Please keep waiting — don't refresh or close this window."
        note="Evaluation can take a little while. Please don't refresh or close this window."
      />
      {loading ? (
        <OverAllLoader message="Loading questions..." />
      ) : !started ? (
        /* ---------------- Instructions screen ---------------- */
        <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: { xs: 2, md: 3 }, background: "radial-gradient(1100px 460px at 50% -8%, #e8f3fd 0%, transparent 60%)" }}>
          <Box sx={{ width: "100%", maxWidth: 780, mb: 1.5 }}>
            <Box onClick={() => navigate(-1)} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, cursor: "pointer", color: C.primary }}>
              <ArrowBackIcon sx={{ fontSize: 20 }} />
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Back</Typography>
            </Box>
          </Box>
          <Paper elevation={0} sx={{ width: "100%", maxWidth: 780, borderRadius: "20px", overflow: "hidden", border: `1px solid ${C.line}`, boxShadow: "0 24px 60px rgba(20,40,80,0.12)" }}>
            {/* Gradient header band */}
            <Box sx={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`, color: "#fff", p: { xs: 3, md: 4 }, display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ width: 56, height: 56, borderRadius: "16px", backgroundColor: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <AssignmentIcon sx={{ fontSize: 30 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 24, fontWeight: 800 }}>Test Instructions</Typography>
                <Typography sx={{ fontSize: 14, opacity: 0.9 }}>
                  {localStorage.getItem("category") || `${getLabel('EXAM')}`} · {isProctored ? "Proctored" : "Self"} {`${getLabel('EXAM')}`} · {time} {Number(time) === 1 ? "minute" : "minutes"}
                </Typography>
              </Box>
            </Box>
            {/* Instruction cards */}
            <Box sx={{ p: { xs: 2.5, md: 4 } }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                {instructions.map((item, i) => (
                  <Box key={i} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", p: 1.75, borderRadius: "12px", border: `1px solid ${C.line}`, backgroundColor: "#fbfdff" }}>
                    <Box sx={{ mt: 0.25, flexShrink: 0 }}>{item.icon}</Box>
                    <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: C.ink }}>{item.text}</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                {secondsToStart > 0 ? (
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontSize: 14, color: C.muted, mb: 1 }}>
                      {`${getLabel('EXAM')}`} starts {startAt.isSame(now, "day") ? `at ${startAt.format("hh:mm A")}` : `on ${startAt.format("DD MMM YYYY, hh:mm A")}`}
                    </Typography>
                    <Typography sx={{ display: "inline-block", fontWeight: 800, fontSize: 22, color: C.primary, backgroundColor: "#e8f3fd", border: "1px solid #cfe6fa", borderRadius: "10px", px: 2, py: 0.75 }}>
                        {formatTime(secondsToStart)}
                    </Typography>
                  </Box>
                ) : (
                  <Tooltip
                    arrow
                    title={entryClosed ? `Start time has passed. Entry closed at ${entryEndAt.format("hh:mm A")}.` : ""}
                  >
                    <span>
                      <Button
                        variant="contained"
                        onClick={handleStartTest}
                        disabled={entryClosed}
                        sx={{ background: entryClosed ? undefined : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`, px: 5, py: 1.25, borderRadius: "12px", textTransform: "none", fontWeight: 800, fontSize: 16, boxShadow: entryClosed ? "none" : "0 10px 26px rgba(28,151,227,0.35)" }}
                      >
                        {isProctored ? "Start Test in Fullscreen" : "Start Test"}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Paper>
        </Box>
      ) : (
        /* ---------------- Exam shell ---------------- */
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
          {/* Top bar */}
          <Box sx={{ position: "sticky", top: 0, zIndex: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, backgroundColor: "#fff", borderBottom: `1px solid ${C.line}`, px: 3, py: 1.25, boxShadow: "0 2px 10px rgba(20,40,80,0.06)" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar sx={{ bgcolor: C.primary, width: 40, height: 40 }}><PersonIcon /></Avatar>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 17 }}>{paper?.title || localStorage.getItem("category")}</Typography>
                <Typography sx={{ fontSize: 13, color: C.muted }}>
                  {paper?.exam_title || `${getLabel('EXAM')}`}{paper?.subject_code ? ` · ${paper.subject_code}` : ""}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 17, color: C.red, backgroundColor: "#fff0f0", border: "1px solid #ffd6d6", borderRadius: "10px", px: 1.5, py: 0.75, animation: timeLeft <= 60 ? "flash 1s infinite" : "none", "@keyframes flash": { "50%": { opacity: 0.5 } } }}>
                ⏱ {formatTime(timeLeft)}
              </Typography>
              <Button variant="contained" disabled={isDisabled} onClick={() => setOpenFinishDialog(true)}
                sx={{ backgroundColor: C.primary, "&:hover": { backgroundColor: C.primaryDark }, textTransform: "none", fontWeight: 700, borderRadius: "10px" }}>
                Submit Test
              </Button>
            </Box>
            {/* answered progress strip */}
            <LinearProgress
              variant="determinate"
              value={totalItems ? (answeredCount / totalItems) * 100 : 0}
              sx={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 3, backgroundColor: "#eef2f7", "& .MuiLinearProgress-bar": { backgroundColor: C.green } }}
            />
          </Box>

          <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
            {/* Sidebar — sections + dot grid */}
            <Box sx={{ width: 260, flexShrink: 0, backgroundColor: "#fff", borderRight: `1px solid ${C.line}`, p: 2, position: "sticky", top: 64, alignSelf: "flex-start", maxHeight: "calc(100vh - 64px)", overflowY: "auto" }}>
              <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.75 }}>
                  <Typography sx={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, color: C.muted, fontWeight: 700 }}>Progress</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: C.primary }}>{answeredCount}/{totalItems}</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={totalItems ? (answeredCount / totalItems) * 100 : 0}
                  sx={{ height: 8, borderRadius: 4, backgroundColor: "#eef2f7", "& .MuiLinearProgress-bar": { backgroundColor: C.green, borderRadius: 4 } }}
                />
              </Box>

              {/* Proctoring webcam — between Progress and the Part A section list. Stays
                  mounted across section changes so the camera isn't reopened each switch. */}
              {cameraAllowed && (
                <Box sx={{ mb: 2.5, position: "relative", borderRadius: "12px", overflow: "hidden", border: `2px solid ${C.primary}`, boxShadow: "0 6px 18px rgba(28,151,227,0.18)" }}>
                  <Webcam
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    mirrored
                    videoConstraints={{ facingMode: "user" }}
                    onUserMedia={handleUserMedia}
                    onUserMediaError={handleUserMediaError}
                    style={{ display: "block", width: "100%", height: 150, objectFit: "cover" }}
                  />
                  <Box sx={{ position: "absolute", top: 6, left: 6, display: "flex", alignItems: "center", gap: 0.5, backgroundColor: "rgba(0,0,0,0.55)", color: "#fff", px: 0.75, py: 0.25, borderRadius: "6px", fontSize: 10, fontWeight: 800, letterSpacing: 0.4 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: C.green, animation: "pulse 1.2s infinite", "@keyframes pulse": { "50%": { opacity: 0.35 } } }} />
                    LIVE
                  </Box>
                </Box>
              )}

              {sections.map((sec, si) => {
                const active = si === activeSection;
                return (
                  <Box key={sec.paper_section_id ?? si} sx={{ mb: 2 }}>
                    <Box onClick={() => { setActiveSection(si); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      sx={{ cursor: "pointer", borderLeft: `3px solid ${active ? C.primary : "transparent"}`, backgroundColor: active ? "#eff6ff" : "transparent", borderRadius: "0 8px 8px 0", px: 1.5, py: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: active ? C.primary : C.ink }}>{sec.section_name}</Typography>
                      <Typography sx={{ fontSize: 11, color: C.muted }}>
                        {(sec.questions?.length || 0)} {sec.pattern === "either-or" ? "either-or" : "questions"}
                        {sec.section_total_marks ? ` · ${sec.section_total_marks} marks` : ""}
                      </Typography>
                    </Box>
                    {/* Always show every part's question-number grid (no click needed). */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1, px: 1.5 }}>
                      {(sec.questions || []).map((item) => {
                        const ans = itemAnswered(item);
                        const fl = flagged[item.number];
                        return (
                          <Tooltip key={item.number} title={ans ? "Answered" : fl ? "Flagged" : "Not answered"} arrow>
                            <Box onClick={() => { setActiveSection(si); scrollToItem(item.number); }}
                              sx={{
                                width: 30, height: 30, borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, cursor: "pointer", border: "1px solid",
                                borderColor: ans ? "#86efac" : fl ? "#fde047" : "#d1d5db",
                                backgroundColor: ans ? C.greenBg : fl ? "#fef9c3" : "#fff",
                                color: ans ? "#166534" : fl ? "#854d0e" : "#374151"
                              }}>
                              {item.number}
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Main — active section's questions (scroll-through), full width */}
            <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, minWidth: 0 }}>
              <Box sx={{ width: "100%" }}>
                {section && (
                  <>
                    <Box sx={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`, borderRadius: "14px", p: 2.5, mb: 3, color: "#fff", boxShadow: "0 8px 24px rgba(28,151,227,0.25)" }}>
                      <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: 0.3 }}>{section.section_name}</Typography>
                      <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.85)", mt: 0.25 }}>
                        {(section.questions?.length || 0)} {section.pattern === "either-or" ? "Either-Or Questions" : "Questions"}
                        {section.section_total_marks ? ` · Total: ${section.section_total_marks} Marks` : ""}
                      </Typography>
                      {(section.instructions || []).length > 0 && (
                        <Box sx={{ mt: 1.5, px: 1.5, py: 0.75, backgroundColor: "rgba(255,255,255,0.18)", borderRadius: "8px", display: "inline-block" }}>
                          <Typography sx={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>{section.instructions.join(" · ")}</Typography>
                        </Box>
                      )}
                      {section.choice && (() => {
                        const must = section.choice.must_answer;
                        const total = section.choice.total_given ?? (section.questions?.length || 0);
                        // Count committed questions the same way the cap does (see isEngaged below)
                        // so the badge and the lock never disagree.
                        const done = (section.questions || []).filter(
                          (it) => itemAnswered(it) || (it.kind === "either-or" && !!eoChoice[it.number])
                        ).length;
                        const reached = done >= must;
                        const word = section.pattern === "either-or" ? "chosen" : "answered";
                        return (
                          <Box sx={{ mt: 1.5, px: 1.5, py: 0.75, backgroundColor: reached ? "rgba(34,197,94,0.28)" : "rgba(255,255,255,0.18)", borderRadius: "8px", display: "inline-block", ml: (section.instructions || []).length > 0 ? 1 : 0 }}>
                            <Typography sx={{ fontSize: 13, color: "#fff", fontWeight: 700 }}>
                              Answer any {must} of {total} questions · {Math.min(done, must)}/{must} {word}{reached ? " ✓" : ""}
                            </Typography>
                          </Box>
                        );
                      })()}
                    </Box>

                    {(() => {
                      const must = section.choice?.must_answer;
                      // A question counts toward the cap once the student COMMITS to it:
                      // fully answered, or (either-or) an option has been picked. This blocks
                      // the remaining questions the moment the must_answer quota is filled.
                      const isEngaged = (it) =>
                        itemAnswered(it) || (it.kind === "either-or" && !!eoChoice[it.number]);
                      const limitReached = must ? (section.questions || []).filter(isEngaged).length >= must : false;
                      return (section.questions || []).map((item) => {
                        const locked = !!must && limitReached && !isEngaged(item);
                        return (
                          <React.Fragment key={item.number}>{renderItem(item, { locked, capped: !!must })}</React.Fragment>
                        );
                      });
                    })()}

                    {/* Section nav */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3, pt: 2, borderTop: `1px solid ${C.line}` }}>
                      <Button disabled={activeSection === 0} onClick={() => { setActiveSection((s) => Math.max(0, s - 1)); window.scrollTo({ top: 0 }); }}
                        sx={{ textTransform: "none", border: `1px solid ${C.line}`, borderRadius: "8px", color: C.ink }}>
                        ← Previous Section
                      </Button>
                      {activeSection < sections.length - 1 ? (
                        <Button onClick={() => { setActiveSection((s) => Math.min(sections.length - 1, s + 1)); window.scrollTo({ top: 0 }); }}
                          sx={{ textTransform: "none", backgroundColor: C.primary, color: "#fff", borderRadius: "8px", "&:hover": { backgroundColor: C.primaryDark } }}>
                          Next Section →
                        </Button>
                      ) : (
                        <Button onClick={() => setOpenFinishDialog(true)}
                          sx={{ textTransform: "none", backgroundColor: C.green, color: "#fff", borderRadius: "8px", "&:hover": { backgroundColor: "#15803d" } }}>
                          Review & Submit
                        </Button>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Warning dialog */}
      <Dialog open={openWarning} onClose={handleCancelMinimize}>
        <DialogTitle>⚠️ Warning</DialogTitle>
        <DialogContent>
          <Typography>If you minimize, exit fullscreen, or switch windows, the test will be auto-submitted. Continue?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelMinimize} sx={{ color: C.primary }}>Continue Test</Button>
          <Button onClick={handleSubmit} color="error">End Test</Button>
        </DialogActions>
      </Dialog>

      {/* Finish / submit dialog */}
      <Dialog open={openFinishDialog} onClose={() => (timeLeft <= 0 ? handleSubmit() : setOpenFinishDialog(false))}>
        <DialogTitle>{timeLeft <= 0 ? "⏳ Time Up!" : "Submit Test?"}</DialogTitle>
        <DialogContent>
          <Typography>
            {timeLeft <= 0
              ? "Your time has ended. Click submit to finish the test."
              : "Are you sure you want to submit the test?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          {timeLeft > 0 && <Button onClick={() => setOpenFinishDialog(false)} sx={{ color: C.primary }}>Continue Test</Button>}
          <Button onClick={handleSubmit} disabled={isDisabled} variant="contained" sx={{ backgroundColor: C.primary, "&:hover": { backgroundColor: C.primaryDark } }}>
            Submit Test
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const cardSx = {
  backgroundColor: "#fff",
  border: "1px solid #eef2f7",
  borderRadius: "14px",
  p: 3,
  mb: 2.5,
  boxShadow: "0 4px 18px rgba(20,40,80,0.06)",
  transition: "box-shadow .15s, transform .15s",
  "&:hover": { boxShadow: "0 8px 26px rgba(28,151,227,0.10)" },
};

export default Test;
